/**
 * Output Variable Lookup Store
 *
 * Provides autocomplete suggestions for Output:Variable and Output:Meter fields.
 * Uses RDD/MDD data from simulation or dictionary generation for accurate,
 * version-specific suggestions.
 *
 * To get suggestions, run a dictionary generation (sizing-only simulation)
 * or a full simulation first.
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { parseRDD, type RDDData, type RDDVariable } from '../simulation/parsers/rddParser';
import { parseMDD, type MDDData, type MDDMeter } from '../simulation/parsers/mddParser';

/**
 * Unified suggestion item for autocomplete
 */
export interface VariableSuggestion {
  /** Variable/meter name (the value to insert) */
  name: string;
  /** Description for display */
  description: string;
  /** Units */
  units: string;
  /** Category for grouping */
  category: string;
  /** Source of the suggestion (always 'simulation' from RDD/MDD) */
  source: 'simulation';
  /** Whether this is model-specific (always true since it comes from simulation) */
  isModelSpecific: boolean;
}

/**
 * Dictionary generation status
 */
export type DictionaryGenerationStatus = 'idle' | 'generating' | 'complete' | 'error';

/**
 * Store state
 */
interface OutputLookupState {
  /** RDD data from simulation (if available) */
  rddData: RDDData | null;
  /** MDD data from simulation (if available) */
  mddData: MDDData | null;
  /** Last updated timestamp */
  lastUpdated: number | null;
  /** Dictionary generation status */
  dictionaryStatus: DictionaryGenerationStatus;
  /** Error message if dictionary generation failed */
  dictionaryError: string | null;
}

/**
 * Store actions
 */
interface OutputLookupActions {
  /** Load RDD file content */
  loadRDD: (content: string) => void;
  /** Load MDD file content */
  loadMDD: (content: string) => void;
  /** Clear simulation data */
  clearSimulationData: () => void;
  /** Get variable suggestions for Output:Variable */
  getVariableSuggestions: (query: string) => VariableSuggestion[];
  /** Get meter suggestions for Output:Meter */
  getMeterSuggestions: (query: string) => VariableSuggestion[];
  /** Set dictionary generation status */
  setDictionaryStatus: (status: DictionaryGenerationStatus, error?: string) => void;
}

/**
 * Convert RDD variable to suggestion
 */
function rddToSuggestion(variable: RDDVariable): VariableSuggestion {
  return {
    name: variable.variableName,
    description: `From simulation RDD (Key: ${variable.keyValue})`,
    units: variable.units,
    category: 'Simulation Results',
    source: 'simulation',
    isModelSpecific: true,
  };
}

/**
 * Convert MDD meter to suggestion
 */
function mddToSuggestion(meter: MDDMeter): VariableSuggestion {
  const flags = [
    meter.meterFileOnly ? 'File Only' : null,
    meter.cumulative ? 'Cumulative' : null,
  ].filter(Boolean).join(', ');
  const description = flags ? `From simulation MDD (${flags})` : 'From simulation MDD';

  return {
    name: meter.meterName,
    description,
    units: meter.units,
    category: 'Simulation Results',
    source: 'simulation',
    isModelSpecific: true,
  };
}


/**
 * Helper to count unique variable names from RDD data
 */
function countUniqueVariables(rddData: RDDData | null): number {
  if (!rddData) return 0;
  const names = new Set<string>();
  for (const variable of rddData.variables) {
    names.add(variable.variableName);
  }
  return names.size;
}

/**
 * Helper to count unique meter names from MDD data
 */
function countUniqueMeters(mddData: MDDData | null): number {
  if (!mddData) return 0;
  const names = new Set<string>();
  for (const meter of mddData.meters) {
    names.add(meter.meterName);
  }
  return names.size;
}

/**
 * Create the store
 */
export const useOutputLookupStore = create<OutputLookupState & OutputLookupActions>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        rddData: null,
        mddData: null,
        lastUpdated: null,
        dictionaryStatus: 'idle',
        dictionaryError: null,

        // Load RDD file content
        loadRDD: (content: string) => {
          const rddData = parseRDD(content);
          set({
            rddData,
            lastUpdated: Date.now(),
            dictionaryStatus: 'complete',
            dictionaryError: null,
          });
        },

        // Load MDD file content
        loadMDD: (content: string) => {
          const mddData = parseMDD(content);
          set({
            mddData,
            lastUpdated: Date.now(),
          });
        },

        // Clear simulation data
        clearSimulationData: () => {
          set({
            rddData: null,
            mddData: null,
            lastUpdated: null,
            dictionaryStatus: 'idle',
            dictionaryError: null,
          });
        },

        // Set dictionary generation status
        setDictionaryStatus: (status, error) => {
          set({
            dictionaryStatus: status,
            dictionaryError: error ?? null,
          });
        },

      // Get variable suggestions for Output:Variable
      getVariableSuggestions: (query: string) => {
        const { rddData } = get();
        const suggestions: VariableSuggestion[] = [];
        const seenNames = new Set<string>();

        if (rddData) {
          const lowerQuery = query.toLowerCase();
          for (const variable of rddData.variables) {
            if (!seenNames.has(variable.variableName)) {
              // Filter by query if provided
              if (!lowerQuery || variable.variableName.toLowerCase().includes(lowerQuery)) {
                suggestions.push(rddToSuggestion(variable));
                seenNames.add(variable.variableName);
              }
            }
          }
        }

        return suggestions;
      },

      // Get meter suggestions for Output:Meter
      getMeterSuggestions: (query: string) => {
        const { mddData } = get();
        const suggestions: VariableSuggestion[] = [];
        const seenNames = new Set<string>();

        if (mddData) {
          const lowerQuery = query.toLowerCase();
          for (const meter of mddData.meters) {
            if (!seenNames.has(meter.meterName)) {
              if (!lowerQuery || meter.meterName.toLowerCase().includes(lowerQuery)) {
                suggestions.push(mddToSuggestion(meter));
                seenNames.add(meter.meterName);
              }
            }
          }
        }

        return suggestions;
      },
    }),
      {
        name: 'output-lookup-storage',
        storage: createJSONStorage(() => sessionStorage),
        // Only persist the actual data, computed values are derived
        partialize: (state) => ({
          rddData: state.rddData,
          mddData: state.mddData,
          lastUpdated: state.lastUpdated,
        }),
      }
    ),
    { name: 'output-lookup-store' }
  )
);

/**
 * Selector: Check if simulation data is available
 * Computed from actual RDD/MDD data, not a stored flag
 */
export function selectHasSimulationData(state: OutputLookupState): boolean {
  return (state.rddData !== null && state.rddData.variables.length > 0) ||
         (state.mddData !== null && state.mddData.meters.length > 0);
}

/**
 * Selector: Get unique variable count
 * Computed from actual RDD data
 */
export function selectVariableCount(state: OutputLookupState): number {
  return countUniqueVariables(state.rddData);
}

/**
 * Selector: Get unique meter count
 * Computed from actual MDD data
 */
export function selectMeterCount(state: OutputLookupState): number {
  return countUniqueMeters(state.mddData);
}

/**
 * Hook to check if simulation data is available
 */
export function useHasSimulationData(): boolean {
  return useOutputLookupStore(selectHasSimulationData);
}

/**
 * Hook to get variable count
 */
export function useVariableCount(): number {
  return useOutputLookupStore(selectVariableCount);
}

/**
 * Hook to get meter count
 */
export function useMeterCount(): number {
  return useOutputLookupStore(selectMeterCount);
}

/**
 * Hook to get variable suggestions
 */
export function useVariableSuggestions(query: string): VariableSuggestion[] {
  return useOutputLookupStore((state) => state.getVariableSuggestions(query));
}

/**
 * Hook to get meter suggestions
 */
export function useMeterSuggestions(query: string): VariableSuggestion[] {
  return useOutputLookupStore((state) => state.getMeterSuggestions(query));
}
